import * as React from 'react';

import {NothingFoundEmptyState} from 'shells/empty-states/nothing-found-empty-state';
import {ErrorState} from 'shells/error-state/error-state';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {Table} from 'shells/table/table';
import {
    convertSortByToUrlSortParam,
    convertUrlSortParamToTableSortBy,
} from 'shells/table/table-sort-helpers';
import type {SortBy} from 'shells/table/types';

import type {FormStubFragment as FormStub} from 'nutshell-graphql-types';

import {EmptyState} from './empty-state';
import {getStagesetLabel} from './helpers';

import styles from './forms-list.module.css';

type Props = {
    isLoading: boolean;
    forms: FormStub[];
    sortString: string;
    isFiltering: boolean;
    isShowingArchived: boolean;
    error: string | null | undefined;
    onChangeSort: (newSortString?: string | null | undefined) => void;
    locationSearch: string;
};

export function FormsTable(props: Props) {
    const columns = React.useMemo(
        () => [
            {
                id: 'name',
                Header: 'Name',
                accessor: (form: FormStub) => form,
                dataType: 'linkedName',
            },
            {
                id: 'submissions',
                Header: '# Submissions',
                accessor: (form: FormStub) => form.numSubmissions,
                dataType: 'integer',
                minWidth: 130,
                width: 130,
                maxWidth: 150,
            },
            {
                id: 'stageset',
                Header: 'Pipeline',
                accessor: (form: FormStub) => getStagesetLabel(form),
                dataType: 'string',
            },
            {
                id: 'pageVisits',
                Header: 'Page visits',
                accessor: (form: FormStub) => form.pageVisits,
                dataType: 'integer',
                minWidth: 100,
                width: 100,
                maxWidth: 100,
            },
            {
                id: 'conversionRate',
                Header: 'Conversion rate',
                accessor: (form: FormStub) => form.conversionRate,
                dataType: 'percentage',
                minWidth: 100,
                width: 100,
                maxWidth: 100,
            },
            {
                id: 'locations',
                Header: '# Locations',
                accessor: (form: FormStub) => form.numLocations,
                dataType: 'integer',
                minWidth: 100,
                width: 100,
                maxWidth: 100,
            },
            {
                id: 'lastSubmission',
                Header: 'Last submission',
                accessor: (form: FormStub) => form.lastSubmittedTime,
                dataType: 'timestamp',
            },
            {
                id: 'createdTime',
                Header: 'Date created',
                accessor: (form: FormStub) => form.createdTime,
                dataType: 'timestamp',
            },
            {
                id: 'lastUpdated',
                Header: 'Last updated',
                accessor: (form: FormStub) =>
                    form.lastUpdatedTime ? form.lastUpdatedTime : undefined,
                dataType: 'timestamp',
                minWidth: 150,
                width: 200,
                maxWidth: 200,
                hasRightBorder: true,
            },
        ],
        []
    );

    const handleSortByChange = (newSortBy: SortBy[]) => {
        const newSortString = convertSortByToUrlSortParam(newSortBy);

        props.onChangeSort(newSortString);
    };

    return (
        <div className={styles['forms-table']}>
            <Table
                isResizable={true}
                hasHeaderBorder={true}
                hasHeaderCellBorders={true}
                hasHoverState={false}
                isCompact={true}
                stickyHeaders={true}
                hasBorders={true}
                isCheckboxSelectable={false}
                clickingSelectsRow={false}
                disableFilters={true}
                disableSorting={false}
                fullWidth={true}
                // Removes "triple sort" and forces there
                // always to be a asc/desc sort applied
                disableSortRemove={true}
                sortBy={convertUrlSortParamToTableSortBy(props.sortString)}
                onChangeSortBy={handleSortByChange}
                shouldPreventSelectAll={false}
                // @ts-expect-error
                columns={columns}
                data={props.forms}
                specialStates={[
                    {
                        shouldRender: () => Boolean(props.error),
                        component: <ErrorState errorMessage={props.error} />,
                    },
                    {
                        shouldRender: () => props.isLoading,
                        component: <LoadingPage padding={0} />,
                    },
                    {
                        shouldRender: () =>
                            !props.forms.length &&
                            !props.isLoading &&
                            (props.isFiltering || props.isShowingArchived),
                        component: (
                            <NothingFoundEmptyState
                                title='No forms were found'
                                subtitle='Try loosening your search!'
                            />
                        ),
                    },
                    {
                        shouldRender: () => !props.forms.length && !props.isLoading,
                        component: <EmptyState locationSearch={props.locationSearch} />,
                    },
                ]}
            />
        </div>
    );
}
